import * as _ from 'lodash';
import * as formatters from './formatters';
import * as editors from './editors';
import { createValidator } from './validators';
import * as plugins from './plugins';
import type { Column } from 'slickgrid';
import { columnFormatterCache } from './columnFormatterCache';

export function rowHeaderColumn(options: Partial<Column>): Column {
  return {
    id: 'rowHeader',
    name: '#',
    field: '$idx',
    behavior: 'select',
    cssClass: 'row-header',
    width: 40,
    cannotTriggerInsert: true,
    resizable: false,
    selectable: false,
    sortable: false,
    filterable: false,
    frozen: true,
    systemColumn: true,
    noExport: true,
    formatter: (row, cell, value, columnDef, data) => {
      return (row + 1).toString();
    },
  };
}

function getCssClass(column: Partial<Column>) {
  const tokens = column.cssClass ? column.cssClass.split(' ') : [];

  if (
    (!column.textAlign && column.type === 'numeric') ||
    column.type === 'right'
  ) {
    tokens.push('align-right');
  } else if (
    !column.textAlign &&
    (column.type === 'date' || column.type === 'datetime')
  ) {
    tokens.push('align-center');
  } else if (column.textAlign) {
    tokens.push(`align-${column.textAlign}`);
  }

  return tokens.join(' ').trim();
}

export function configure(
  column: Partial<Column>,
  defaults?: Partial<Column>,
): Column {
  if (column.type === 'rowHeader') {
    return rowHeaderColumn(column);
  }

  if (column.type === 'command') {
    column = new plugins.CommandColumn(column);
  }

  if (column.type !== 'rowHeader' && !column.field) {
    throw new TypeError('missing `field` configuration [slickg.column]');
  }

  if (column.type !== 'rowHeader' && column.id == null) {
    column.id = column.field;
  }

  for (const prop of ['width', 'minWidth', 'maxWidth'] as const) {
    if (typeof column[prop] === 'string') {
      column[prop] =
        parseInt(column[prop] as unknown as string, 10) || undefined;
    }
  }

  column.formatter = columnFormatterCache(
    formatters.getColumnFormatter(column as Column),
    column,
  );

  if (_.isString(column.editor)) {
    const editorFactory = editors[column.editor];

    if (!editorFactory) {
      throw new TypeError(`invalid editor: ${column.editor} [slickg.column]`);
    }
    column.editor = editorFactory(column);
  }

  if (column.validators) {
    if (_.isArray(column.validators)) {
      column.validator = createValidator(...column.validators);
    } else if (_.isString(column.validators)) {
      column.validator = createValidator(column.validators);
    }
  }

  if (!column.textAlign) {
    setTextAlign(column);
  }

  column.cssClass = getCssClass(column);

  if (defaults) {
    mergeInDefaults(column, defaults);
  }

  return column as Column;
}

function mergeInDefaults(col: Partial<Column>, defaults: Partial<Column>) {
  _.each(defaults, (v, k) => {
    if (_.isUndefined(col[k])) {
      col[k] = v;
    }
  });

  return col;
}

function setTextAlign(column: Partial<Column>) {
  if (column.type && ['number', 'integer'].indexOf(column.type) > -1) {
    column.textAlign = 'right';
    return;
  }

  if (column.format && ['date', 'date-time'].indexOf(column.format) > -1) {
    column.textAlign = 'center';
  }
}
